'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from '@/lib/api';
import { cn } from '@/lib/utils';

type SocialAuthButtonsProps = {
  intent: 'login' | 'register' | 'link';
  returnTo?: string;
  className?: string;
  providers?: { google: boolean; vk: boolean; telegram: boolean; max: boolean };
};

declare global {
  interface Window {
    __lisadTelegramAuthCallback?: (user: Record<string, unknown>) => void;
  }
}

const TELEGRAM_CALLBACK_NAME = '__lisadTelegramAuthCallback';
const SOCIAL_LINK_EXISTING_FLAG_KEY = 'lisad_social_link_existing';
const socialButtonBase =
  'group relative flex w-full items-center gap-3 overflow-hidden rounded-2xl border px-4 py-3.5 text-left transition-all duration-300 disabled:cursor-not-allowed disabled:opacity-55';
const socialBadgeBase =
  'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-site-border bg-site-surface/[0.08] shadow-[0_10px_24px_rgba(2,6,23,0.32)]';

function normalizeIntent(intent: SocialAuthButtonsProps['intent']): 'login' | 'link' {
  return intent === 'link' ? 'link' : 'login';
}

function GoogleIcon({ className = '' }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
      <path
        fill="#FFC107"
        d="M43.61 20.08H42V20H24v8h11.3C33.65 32.66 29.2 36 24 36c-6.63 0-12-5.37-12-12s5.37-12 12-12c3.06 0 5.84 1.15 7.96 3.04l5.66-5.66C34.05 6.05 29.27 4 24 4 12.95 4 4 12.95 4 24s8.95 20 20 20 20-8.95 20-20c0-1.34-.14-2.65-.39-3.92Z"
      />
      <path
        fill="#FF3D00"
        d="M6.31 14.69l6.57 4.82C14.66 15.09 18.98 12 24 12c3.06 0 5.84 1.15 7.96 3.04l5.66-5.66C34.05 6.05 29.27 4 24 4c-7.68 0-14.35 4.34-17.69 10.69Z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.17 0 9.86-1.98 13.41-5.2l-6.19-5.24C29.17 35.12 26.68 36 24 36c-5.18 0-9.62-3.31-11.27-7.92l-6.52 5.02C9.51 39.57 16.2 44 24 44Z"
      />
      <path
        fill="#1976D2"
        d="M43.61 20.08H42V20H24v8h11.3c-.79 2.24-2.22 4.16-4.08 5.56l6.19 5.24C36.97 39.2 44 34 44 24c0-1.34-.14-2.65-.39-3.92Z"
      />
    </svg>
  );
}

function VkIcon({ className = '' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path
        fill="currentColor"
        d="M3.35 7.76c.13 6.43 3.35 10.3 9 10.3h.32v-3.66c2.02.2 3.55 1.67 4.16 3.66H20c-.79-2.86-2.86-4.44-4.16-5.04 1.3-.74 3.1-2.53 3.53-5.26h-2.89c-.56 2.22-2.2 4.01-3.81 4.2V7.76h-2.9v7.36c-1.63-.4-3.68-2.31-3.78-7.36H3.35Z"
      />
    </svg>
  );
}

function TelegramIcon({ className = '' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path
        fill="currentColor"
        d="M21.65 3.51a1.28 1.28 0 0 0-1.32-.17L2.8 10.74a1.28 1.28 0 0 0 .1 2.41l4.45 1.6 1.73 5.24a1.28 1.28 0 0 0 2.16.48l2.5-2.56 4.1 3.02a1.28 1.28 0 0 0 2-.74l2.3-15.3c.08-.54-.12-1.06-.5-1.38Zm-3.95 4.04-8.02 7.8-.67-2.02 6.88-5.78a.51.51 0 1 0-.66-.78l-8.39 5.6-2.6-.93 14.56-6.14-1.1 7.25Z"
      />
    </svg>
  );
}

function MaxIcon({ className = '' }: { className?: string }) {
  return <span className={`font-black tracking-[-0.08em] ${className}`} aria-hidden="true">M</span>;
}

export default function SocialAuthButtons({
  intent,
  returnTo = '/',
  className = '',
  providers = { google: false, vk: false, telegram: false, max: false },
}: SocialAuthButtonsProps) {
  const [loadingProvider, setLoadingProvider] = useState<'google' | 'vk' | 'telegram' | 'max' | null>(null);
  const [error, setError] = useState('');
  // Привязка включена по умолчанию; на странице завершения входа пользователь может пропустить её.
  const linkExistingRequested = true;
  const googleAvailable = providers.google;
  const vkAvailable = providers.vk;
  const telegramAvailable = providers.telegram;
  const maxAvailable = providers.max;
  const telegramBot = (process.env.NEXT_PUBLIC_TELEGRAM_BOT_USERNAME || '').trim();
  const maxBot = (process.env.NEXT_PUBLIC_MAX_BOT_USERNAME || '').trim().replace(/^@/, '');
  const effectiveIntent = useMemo(() => normalizeIntent(intent), [intent]);
  const canRequestExistingLink = intent === 'login' || intent === 'register';
  const syncExistingLinkPreference = useCallback((enabled: boolean) => {
    try {
      if (enabled) {
        window.sessionStorage.setItem(SOCIAL_LINK_EXISTING_FLAG_KEY, '1');
      } else {
        window.sessionStorage.removeItem(SOCIAL_LINK_EXISTING_FLAG_KEY);
      }
    } catch {
      // ignore storage errors
    }
  }, []);

  const startOauth = async (provider: 'google' | 'vk' | 'max') => {
    setError('');
    syncExistingLinkPreference(canRequestExistingLink && linkExistingRequested);
    setLoadingProvider(provider);
    try {
      const resp = await apiClient.startSocialAuth(provider, effectiveIntent, returnTo);
      if (!resp.auth_url) throw new Error('Не удалось получить URL авторизации');
      window.location.href = resp.auth_url;
    } catch (e: any) {
      setError(e?.message || `Не удалось запустить ${provider} авторизацию`);
      setLoadingProvider(null);
    }
  };

  useEffect(() => {
    syncExistingLinkPreference(canRequestExistingLink && linkExistingRequested);
  }, [canRequestExistingLink, linkExistingRequested, syncExistingLinkPreference]);

  useEffect(() => {
    window[TELEGRAM_CALLBACK_NAME] = async (telegramUser: Record<string, unknown>) => {
      setError('');
      syncExistingLinkPreference(canRequestExistingLink && linkExistingRequested);
      setLoadingProvider('telegram');
      try {
        const res = await apiClient.verifyTelegramAuth(telegramUser, effectiveIntent, returnTo);
        if (res.merge_required && res.merge_token) {
          window.location.href = `/profile/settings#account-merge=${encodeURIComponent(res.merge_token)}`;
          return;
        }
        const exchange = res.exchange_token;
        if (!exchange) throw new Error('Не удалось завершить Telegram авторизацию');
        window.location.href = `/auth/social/finish?exchange=${encodeURIComponent(exchange)}&return_to=${encodeURIComponent(returnTo)}`;
      } catch (e: any) {
        setError(e?.message || 'Не удалось завершить Telegram авторизацию');
        setLoadingProvider(null);
      }
    };

    return () => {
      if (window[TELEGRAM_CALLBACK_NAME]) {
        delete window[TELEGRAM_CALLBACK_NAME];
      }
    };
  }, [canRequestExistingLink, effectiveIntent, linkExistingRequested, returnTo, syncExistingLinkPreference]);

  useEffect(() => {
    if (!telegramBot) return;
    const container = document.getElementById('telegram-login-widget-container');
    if (!container) return;
    container.innerHTML = '';

    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.async = true;
    script.setAttribute('data-telegram-login', telegramBot);
    script.setAttribute('data-size', 'large');
    script.setAttribute('data-userpic', 'false');
    script.setAttribute('data-radius', '8');
    script.setAttribute('data-request-access', 'write');
    script.setAttribute('data-onauth', `${TELEGRAM_CALLBACK_NAME}(user)`);
    container.appendChild(script);
  }, [telegramBot]);

  return (
    <div className={`space-y-3 ${className}`}>
      {googleAvailable && (
        <button
          type="button"
          className={cn(
            socialButtonBase,
            'border-site-accent/45 bg-site-raised/50 text-site-text hover:border-site-accent/70 hover:bg-site-raised'
          )}
          disabled={loadingProvider !== null}
          onClick={() => void startOauth('google')}
        >
          <span className={cn(socialBadgeBase, 'bg-site-surface text-site-muted')}>
            <GoogleIcon className="h-5 w-5" />
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="text-sm font-semibold tracking-[0.02em]">Google</span>
            <span className="truncate text-xs text-site-text/90">Продолжить с Google</span>
          </span>
          <span className="text-[11px] uppercase tracking-[0.2em] text-site-text/70">
            {loadingProvider === 'google' ? 'Loading' : 'OAuth'}
          </span>
        </button>
      )}

      {vkAvailable && <button
        type="button"
        className={cn(
          socialButtonBase,
          'border-site-accent/35 bg-site-raised/50 text-site-text'
        )}
        disabled={loadingProvider !== null}
        onClick={() => {
          if (!vkAvailable) return;
          void startOauth('vk');
        }}
      >
        <span className={cn(socialBadgeBase, 'bg-site-accent/18 text-site-accent')}>
          <VkIcon className="h-5 w-5" />
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="text-sm font-semibold tracking-[0.02em]">VK</span>
          <span className="truncate text-xs text-site-secondary">{vkAvailable ? 'Продолжить с VK' : 'VK временно недоступен'}</span>
        </span>
        <span className="text-[11px] uppercase tracking-[0.2em] text-site-muted">
          {loadingProvider === 'vk' ? 'Loading' : 'OAuth'}
        </span>
        </button>}

      {maxAvailable && maxBot ? (
        <button
          type="button"
          className={cn(
            socialButtonBase,
            'border-site-accent/35 bg-site-raised/50 text-site-text hover:border-site-accent/70 hover:bg-site-raised'
          )}
          disabled={loadingProvider !== null}
          onClick={() => void startOauth('max')}
        >
          <span className={cn(socialBadgeBase, 'bg-[#111827] text-white')}>
            <MaxIcon className="text-lg" />
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="text-sm font-semibold tracking-[0.02em]">MAX</span>
            <span className="truncate text-xs text-site-secondary">
              {intent === 'link' ? 'Привязать или объединить с MAX' : 'Продолжить через MAX'}
            </span>
          </span>
          <span className="text-[11px] uppercase tracking-[0.2em] text-site-muted">
            {loadingProvider === 'max' ? 'Loading' : 'Mini app'}
          </span>
        </button>
      ) : null}

      {telegramAvailable && telegramBot ? (
        <div className="space-y-2">
          <div
            id="telegram-login-widget-container"
            className="min-h-[48px] rounded-xl border border-site-border bg-site-surface/[0.04] px-2 py-2 text-center [&_iframe]:mx-auto"
          />
          {loadingProvider === 'telegram' && (
            <p className="mt-2 text-xs text-site-secondary">Завершаем вход через Telegram...</p>
          )}
          {intent === 'link' && loadingProvider !== 'telegram' && (
            <p className="text-center text-xs text-site-muted">Привязать или объединить с Telegram</p>
          )}
        </div>
      ) : null}

      {error && (
        <div className="p-3 rounded-lg border border-site-danger/50 bg-site-danger/10">
          <p className="text-sm text-site-danger">{error}</p>
        </div>
      )}
    </div>
  );
}
